import { Routes, Route, NavLink, Navigate } from "react-router-dom";
import {
  MasterCrudPage,
  useRouteModulePermissions,
} from "@/components/common/Crud";
import Enquiries, { EnquiryDetail } from "@/pages/Enquiries";

function SubNav({ items }) {
  return (
    <div className="border-b border-slate-200 mb-3 flex flex-wrap gap-1 text-xs">
      {items.map((i) => (
        <NavLink
          key={i.to}
          to={i.to}
          end={i.end}
          data-testid={`subnav-${i.to.split("/").pop() || "root"}`}
          className={({ isActive }) =>
            `px-3 py-1.5 border-b-2 -mb-px ${isActive ? "border-blue-600 text-blue-600 font-medium" : "border-transparent text-slate-600 hover:text-slate-900"}`
          }
        >
          {i.label}
        </NavLink>
      ))}
    </div>
  );
}

export default function FrontOffice() {
  const enquiriesPermissions = useRouteModulePermissions(
    "front_office",
    "enquiries",
  );
  const applicationsPermissions = useRouteModulePermissions(
    "front_office",
    "applications",
  );
  const phonePermissions = useRouteModulePermissions(
    "front_office",
    "phone_call_logs",
  );
  const dispatchPermissions = useRouteModulePermissions(
    "front_office",
    "postal_dispatch",
  );
  const receivePermissions = useRouteModulePermissions(
    "front_office",
    "postal_receive",
  );
  const complaintsPermissions = useRouteModulePermissions(
    "front_office",
    "complaints",
  );
  const lostFoundPermissions = useRouteModulePermissions(
    "front_office",
    "lost_found",
  );
  const items = [
    enquiriesPermissions.canView && {
      to: "/front-office/enquiries",
      label: "Admission Enquiry",
      end: false,
    },
    applicationsPermissions.canView && {
      to: "/front-office/applications",
      label: "Applications",
    },
    phonePermissions.canView && {
      to: "/front-office/phone-calls",
      label: "Phone Call Log",
    },
    dispatchPermissions.canView && {
      to: "/front-office/postal-dispatch",
      label: "Postal Dispatch",
    },
    receivePermissions.canView && {
      to: "/front-office/postal-receive",
      label: "Postal Receive",
    },
    complaintsPermissions.canView && {
      to: "/front-office/complaints",
      label: "Complaints",
    },
    lostFoundPermissions.canView && {
      to: "/front-office/lost-found",
      label: "Lost & Found",
    },
  ].filter(Boolean);
  if (items.length === 0) {
    return (
      <div
        data-testid="front-office-page"
        className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500"
      >
        No front office workflow assigned for this role.
      </div>
    );
  }
  return (
    <div data-testid="front-office-page">
      <SubNav items={items} />
      <Routes>
        <Route index element={<Navigate to={items[0].to} replace />} />
        {enquiriesPermissions.canView && (
          <Route path="enquiries" element={<Enquiries />} />
        )}
        {enquiriesPermissions.canView && (
          <Route path="enquiries/:id" element={<EnquiryDetail />} />
        )}
        {applicationsPermissions.canView && (
          <Route
            path="applications"
            element={
              <MasterCrudPage
                title="Applications"
                endpoint="/student-applications"
                testid="apps"
                dateField="date"
                moduleKey="front_office"
                submoduleKey="applications"
                fields={[
                  { key: "name", label: "Student Name", required: true },
                  { key: "class", label: "Class" },
                  { key: "father_name", label: "Father" },
                  { key: "mother_name", label: "Mother" },
                  { key: "contact", label: "Contact" },
                  { key: "date", label: "Date", type: "date" },
                  {
                    key: "status",
                    label: "Status",
                    type: "select",
                    options: [
                      { value: "pending", label: "Pending" },
                      { value: "verified", label: "Verified" },
                      { value: "rejected", label: "Rejected" },
                    ],
                  },
                ]}
              />
            }
          />
        )}
        {phonePermissions.canView && (
          <Route
            path="phone-calls"
            element={
              <MasterCrudPage
                title="Phone Call Log"
                endpoint="/phone-call-logs"
                testid="phone-calls"
                dateField="date"
                moduleKey="front_office"
                submoduleKey="phone_call_logs"
                fields={[
                  {
                    key: "call_type",
                    label: "Type",
                    type: "select",
                    options: [
                      { value: "in", label: "Incoming" },
                      { value: "out", label: "Outgoing" },
                    ],
                  },
                  { key: "name", label: "Contact Name" },
                  { key: "phone", label: "Phone" },
                  { key: "date", label: "Date", type: "date" },
                  { key: "duration", label: "Duration (min)", type: "number" },
                  {
                    key: "description",
                    label: "Description",
                    type: "textarea",
                    full: true,
                  },
                  { key: "next_action", label: "Next Action", full: true },
                ]}
              />
            }
          />
        )}
        {dispatchPermissions.canView && (
          <Route
            path="postal-dispatch"
            element={
              <MasterCrudPage
                title="Postal Dispatch"
                endpoint="/postal-dispatch"
                testid="postal-d"
                dateField="date"
                moduleKey="front_office"
                submoduleKey="postal_dispatch"
                fields={[
                  { key: "name", label: "To Name" },
                  {
                    key: "address",
                    label: "Address",
                    type: "textarea",
                    full: true,
                  },
                  { key: "date", label: "Date", type: "date" },
                  { key: "reference", label: "Reference" },
                  { key: "from_title", label: "From Title" },
                ]}
              />
            }
          />
        )}
        {receivePermissions.canView && (
          <Route
            path="postal-receive"
            element={
              <MasterCrudPage
                title="Postal Receive"
                endpoint="/postal-receive"
                testid="postal-r"
                dateField="date"
                moduleKey="front_office"
                submoduleKey="postal_receive"
                fields={[
                  { key: "name", label: "From Name" },
                  {
                    key: "address",
                    label: "Address",
                    type: "textarea",
                    full: true,
                  },
                  { key: "date", label: "Date", type: "date" },
                  { key: "reference", label: "Reference" },
                  { key: "to_title", label: "To Title" },
                ]}
              />
            }
          />
        )}
        {complaintsPermissions.canView && (
          <Route
            path="complaints"
            element={
              <MasterCrudPage
                title="Complaints"
                endpoint="/complaints"
                testid="complaints"
                dateField="date"
                moduleKey="front_office"
                submoduleKey="complaints"
                fields={[
                  { key: "name", label: "Complainant" },
                  { key: "type", label: "Type" },
                  { key: "phone", label: "Phone" },
                  { key: "date", label: "Date", type: "date" },
                  {
                    key: "status",
                    label: "Status",
                    type: "select",
                    options: [
                      { value: "open", label: "Open" },
                      { value: "resolved", label: "Resolved" },
                    ],
                  },
                  {
                    key: "description",
                    label: "Description",
                    type: "textarea",
                    full: true,
                  },
                  {
                    key: "action_taken",
                    label: "Action Taken",
                    type: "textarea",
                    full: true,
                  },
                ]}
              />
            }
          />
        )}
        {lostFoundPermissions.canView && (
          <Route
            path="lost-found"
            element={
              <MasterCrudPage
                title="Lost & Found"
                endpoint="/lost-found"
                testid="lost-found"
                dateField="found_on"
                moduleKey="front_office"
                submoduleKey="lost_found"
                fields={[
                  { key: "name", label: "Item Name", required: true },
                  { key: "category", label: "Category" },
                  { key: "found_on", label: "Found On", type: "date" },
                  { key: "location", label: "Location" },
                  {
                    key: "status",
                    label: "Status",
                    type: "select",
                    options: [
                      { value: "open", label: "Unclaimed" },
                      { value: "claimed", label: "Claimed" },
                    ],
                  },
                  {
                    key: "description",
                    label: "Description",
                    type: "textarea",
                    full: true,
                  },
                ]}
              />
            }
          />
        )}
      </Routes>
    </div>
  );
}
